<template>
  <div class="flex justify-between border-gray-200">
    <h1 class="font-semibold text-3xl mb-1">Payments</h1>
    <DownloadButton
      title="Download CSV"
      label="Download"
      :downloading="isDownloadingCsv"
      :disabled="payments.length === 0"
      @click="downloadCsv"
    />
  </div>
  <div class="bg-white border border-gray-200 rounded-lg mt-4">
    <PaymentsControls
      @update:status="onStatusChange"
      @update:type="onTypeChange"
      @update:method="onMethodChange"
      @update:dateRange="onDateRangeChange"
      @update:clear-filters="onClearFilters"
      @update:search-term="onSearchTermChange"
    />
    <DataTable
      :is-loading="isLoading"
      :columns="paymentsRegisterColumns"
      :data="payments"
      :total-count="totalCount"
      :page-number="pageNumber"
      :manual-sorting="true"
      @update:page="onUpdatePage"
      @update:page-size="onUpdatePageSize"
      @update:sorting="onSortingChange"
    />
  </div>
</template>

<script setup lang="ts">
import DownloadButton from '@/components/common/DownloadButton.vue';
import { NetworkError } from '@/errors';
import type { NewPayment, PaymentMethod, PaymentParams, PaymentStatus, PaymentType } from '@/models/payments';
import { fetchPayments } from '@/services/payments';
import { useGeneralStore } from '@/stores/general';
import { fetchAllPaginated, generateCsvBlobUrl } from '@/utils/export.utils';
import { toRandString } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { deepEqual } from '@/utils/paging.utils';
import dayjs from 'dayjs';
import type { DateRange } from 'radix-vue';
import { onMounted, ref } from 'vue';
import type { ModelPaymentResponse } from '@layup/api-sdk';
import DataTable from '../../DataTable.vue';
import PaymentsControls from '../payments-controls/PaymentsControls.vue';
import { paymentsRegisterColumns } from './PaymentTableColumns';

const generalStore = useGeneralStore();

const isLoading = ref(false);
const paymentParams = ref<PaymentParams>({
  state: 'all',
  sort: '-due',
  skip: 0,
});
const previousParams = ref<PaymentParams>({ ...paymentParams.value });
const payments = ref<NewPayment[]>([]);

const totalCount = ref(1);
const pageNumber = ref(1);

onMounted(async () => onUpdateFilter({ ...paymentParams.value }, true));

const onUpdateFilter = async (params: PaymentParams, skipCheck: boolean = false) => {
  if (!skipCheck) {
    if (deepEqual(params, previousParams.value)) {
      return;
    }
    previousParams.value = { ...params };
  }

  payments.value = [];
  isLoading.value = true;

  try {
    params.limit = generalStore.pageSize;
    params.skip = (pageNumber.value - 1) * generalStore.pageSize;
    const response = await fetchPayments(params);
    payments.value = response.payments || [];
    totalCount.value = response.totalCount || 0;
    addMetric('payment-register-table', { status: 'success', channel: 'merchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('payment-register-table', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('payment-register-table', { status: 'error', channel: 'merchant' });
    }
  } finally {
    isLoading.value = false;
  }
};

const onStatusChange = (value: PaymentStatus) => {
  paymentParams.value.state = value;
  // Need to clone the object to avoid mutating the original
  onUpdateFilter({ ...paymentParams.value });
};

const onTypeChange = (value: PaymentType) => {
  paymentParams.value.type = value === 'ALL' ? '' : value;
  onUpdateFilter({ ...paymentParams.value });
};

const onMethodChange = (value: PaymentMethod) => {
  paymentParams.value.method = value === 'ALL' ? '' : value;
  onUpdateFilter({ ...paymentParams.value });
};

const onDateRangeChange = (value: DateRange): void => {
  paymentParams.value.from = new Date(`${value.start?.year}-${value.start?.month}-${value.start?.day}`);
  paymentParams.value.to = new Date(`${value.end?.year}-${value.end?.month}-${value.end?.day}`);
  onUpdateFilter({ ...paymentParams.value });
};

const onClearFilters = (): void => {
  pageNumber.value = 1;
  paymentParams.value = {
    state: 'all',
    sort: '-due',
    skip: 0,
  };
  onUpdateFilter({ ...paymentParams.value });
};

const onSearchTermChange = (value: string): void => {
  paymentParams.value.search = value;
  onUpdateFilter({ ...paymentParams.value });
};

const onUpdatePage = (pageNo: number) => {
  pageNumber.value = pageNo;
  paymentParams.value.skip = (pageNumber.value - 1) * generalStore.pageSize;
  onUpdateFilter({ ...paymentParams.value });
};

const onUpdatePageSize = (size: number) => {
  pageNumber.value = 1;
  generalStore.pageSize = size;
  paymentParams.value.limit = size;
  onUpdateFilter({ ...paymentParams.value });
};

const isDownloadingCsv = ref(false);

const downloadCsv = async () => {
  isDownloadingCsv.value = true;
  try {
    const payments = await fetchAllPaginated<ModelPaymentResponse, PaymentParams>(
      fetchPayments,
      paymentParams.value,
      'payments',
    );
    if (payments.length === 0) {
      console.warn('No data available for export');
      return;
    }

    const headerMap: Partial<Record<keyof NewPayment, string>> = {
      client: 'Customer Name',
      amount: 'Amount',
      createdAt: 'Created Date',
      dateRefunded: 'Refunded Date',
      due: 'Due Date',
      fee: 'Fee',
      locked: 'Locked',
      orderName: 'Order Name',
      orderReference: 'Order Reference',
      paymentStatus: 'Payment Status',
      paymentType: 'Payment Type',
      settlementBatch: 'Settlement Batch',
      state: 'Status',
      updatedAt: 'Updated Date',
      datePaid: 'Paid Date',
    };

    const data = payments.map((payment) => ({
      ...payment,
      amount: toRandString(payment.amount ?? 0),
      fee: toRandString(payment.fee ?? 0),
    }));

    const url = generateCsvBlobUrl(headerMap, data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `payments-${dayjs().format('YYYY-MM-DD')}.csv`);
    link.click();
    URL.revokeObjectURL(url);
  } finally {
    isDownloadingCsv.value = false;
  }
};

const columnToApiFieldMap: Record<string, string> = {
  due: 'dueAt',
  verifiedAt: 'verifiedAt',
  amount: 'amount',
};

const onSortingChange = (sortingState: Array<{ id: string; desc: boolean }>) => {
  if (sortingState.length === 0) {
    delete paymentParams.value.sort;
  } else {
    const { id, desc } = sortingState[0]; // TanStack table only supports single column sorting by default
    const apiField = columnToApiFieldMap[id] || id;
    paymentParams.value.sort = desc ? `-${apiField}` : apiField;
  }
  pageNumber.value = 1; // Reset to first page when sorting changes
  onUpdateFilter({ ...paymentParams.value });
};
</script>
